import { EmulatorType, LocalStackInstance, LocalstackPlatformPlugin, ProductFeature } from '@localstack/types';
import { ReactElement } from 'react';

import { INSTANCE_ROUTES } from '@localstack/constants';
import { buildRoute, useEntitledEmulators } from '@localstack/services';
import { matchPath, useLocation } from 'react-router-dom';
import type { UseAuthProvider } from '@localstack/types';

import { BaseTopMenu, TopMenuLinkType } from '../navigation';

export type TopMenuSwitcherProps = {
  instance?: LocalStackInstance;
  disabledRoutes?: string[];
  useAuthProvider: UseAuthProvider;
  /**
   * Wraps the App Inspector tab button, e.g. to attach the visibility-experiment nudge.
   */
  appInspectorNudge?: (button: ReactElement) => ReactElement;
};
type InstanceRoutePath = (typeof INSTANCE_ROUTES)[keyof typeof INSTANCE_ROUTES];

type InstanceEntriesDefinition = {
  name: string;
  route: InstanceRoutePath;
  secondaryRoute?: InstanceRoutePath;
  display?: boolean;
  renderButton?: (button: ReactElement) => ReactElement;
};

export const TopMenuSwitcher = ({
  instance,
  disabledRoutes,
  useAuthProvider,
  appInspectorNudge,
}: TopMenuSwitcherProps): ReactElement => {
  const emulators = useEntitledEmulators({ useAuthProvider });
  const showSnowflake = emulators.includes(EmulatorType.SNOWFLAKE);
  const { hasFeature } = useAuthProvider();
  const { pathname } = useLocation();

  const entries: InstanceEntriesDefinition[] = [
    {
      name: 'Overview',
      route: INSTANCE_ROUTES.STACK_OVERVIEW,
    },
    {
      name: 'Status',
      route: INSTANCE_ROUTES.STATUS,
    },
    {
      name: 'Resource Browser',
      route: INSTANCE_ROUTES.RESOURCES_OVERVIEW,
      secondaryRoute: INSTANCE_ROUTES.RESOURCES_COMMON,
    },
    {
      name: 'State',
      route: INSTANCE_ROUTES.INSTANCE_STATE,
    },
    {
      name: 'App Inspector',
      route: INSTANCE_ROUTES.APP_INSPECTOR_SPANS_LIST,
      display: hasFeature(
        ProductFeature.FEATURE_LOCALSTACK_PLUGIN,
        LocalstackPlatformPlugin.LOCALSTACK_PLATFORM_PLUGIN_APPINSPECTOR,
      ),
      renderButton: appInspectorNudge,
    },
    {
      name: 'IAM Policy Stream',
      route: INSTANCE_ROUTES.IAM_POLICY_STREAM,
    },
    {
      name: 'Chaos Engineering',
      route: INSTANCE_ROUTES.CHAOS_ENGINEERING,
    },
    {
      name: 'Logs',
      route: INSTANCE_ROUTES.INSTANCE_LOGS,
      display: !!instance?.ephemeralInstance,
    },
    {
      name: 'Snowflake',
      route: INSTANCE_ROUTES.INSTANCE_SNOWFLAKE,
      display: showSnowflake || (disabledRoutes?.length || 0) > 0, // hideous, will have to find something better
    },
    {
      name: 'Extensions',
      route: INSTANCE_ROUTES.EXTENSIONS_MANAGE,
      secondaryRoute: INSTANCE_ROUTES.EXTENSIONS_DETAILS,
    },
  ];

  const links: TopMenuLinkType[] = entries
    .filter((entry) => {
      const isDisabled =
        disabledRoutes?.includes(entry.route) ||
        (entry.secondaryRoute && disabledRoutes?.includes(entry.secondaryRoute));

      const isHidden = entry.display === false;

      return !isDisabled && !isHidden;
    })
    .map((entry) => {
      const isActive =
        !!matchPath({ path: entry.route, end: false }, pathname) ||
        (!!entry.secondaryRoute && !!matchPath({ path: entry.secondaryRoute, end: false }, pathname));

      return {
        label: entry.name,
        href: buildRoute(entry.route, { iid: instance?.id }),
        isActive,
        renderButton: entry.renderButton,
      };
    });

  return <BaseTopMenu links={links} />;
};
